import { gql } from '@apollo/client';

/**
 * The client's NACH mandates — the instrument a rebalance's buys are debited
 * from. Only an APPROVED one can fund anything; the rest are listed so a
 * mandate still being registered explains itself rather than being missing.
 */
export const GET_USER_MANDATES = gql`
  query GetUserMandates {
    getUserMandates {
      id
      umrn
      mandateType
      mandateStatus
      mandateAmount
      startDate
      endDate
      accountNumber
      ifscCode
      branchName
    }
  }
`;

/**
 * A client's trading accounts of one type — the goal and the MANDATE it runs
 * under: capital, target, philosophy, benchmark and the loss limits every
 * order on the account is checked against (backend `TradingMandate`).
 */
export const GET_TRADING_GOALS = gql`
  query GetTradingGoals($goalType: TradingGoalTypeEnum!) {
    getTradingGoal(goalType: $goalType) {
      userinvestmentgoalId
      goalType
      capital
      targetReturnPct
      targetCapital
      monthsToTarget
      philosophy
      measuringBenchmark
      allowedAssetClasses
      maxLossPerTradePct
      maxLossPerPositionPct
      maxDailyLossPct
      maxDrawdownPct
      maxPositionPct
      maxOpenPositions
      stopLossRequired
      maxLeverage
      minHoldingDays
      maxHoldingDays
      maxDaysToExit
      trackingErrorMaxPct
      maxTradesPerDay
      squareOffTime
      maxMarginUtilisationPct
      maxLotsPerTrade
      hedgedOnly
      basketStyle
      theme
      factors
      minNames
      maxWeightPerNamePct
      rebalanceFrequency
      goal { userinvestmentgoalId name status }
    }
  }
`;

/**
 * A client's trading accounts as they stand: each account's positions and their
 * profit or loss, its value over time against its benchmark, the money paid in
 * and what it bought, and how much of each mandate limit is in use. Read-only.
 */
export const GET_TRADE_PORTFOLIO = gql`
  query GetTradePortfolio($clientUserId: Int) {
    getTradePortfolio(clientUserId: $clientUserId) {
      clientUserId
      totals { accounts capitalInr investedInr valueInr pnlInr returnPctOfCapital openPositions }
      accounts {
        goalId
        goalType
        name
        portfolioSymbol
        benchmark
        philosophy
        riskStatus
        totals { capitalInr investedInr valueInr pnlInr pnlPctOfInvested returnPctOfCapital openPositions }
        positions { symbol quantity averagePrice lastPrice costInr valueInr pnlInr pnlPct weightPct priceIsStale firstBought daysHeld }
        risk { key label used limit unit shareOfLimitPct status }
        history {
          moneyAddedInr
          moneyWithdrawnInr
          boughtInr
          soldInr
          events { date kind symbol quantity price amountInr note }
        }
        activity { date moneyInInr inStocksInr boughtInr soldInr trades }
        typeView { goalType about facts { key label value unit used limit shareOfLimitPct status } }
      }
      fundTransfers { date kind amountInr status method }
    }
  }
`;

/**
 * A stock's last price and its nearest supports and resistances (three each)
 * from its own chart — the levels a held position is read against.
 */
export const GET_TRADE_STOCK_LEVELS = gql`
  query TradeStockLevels($symbol: String!) {
    getTradeStockLevels(symbol: $symbol) {
      symbol
      price
      asOf
      supports { price label touches }
      resistances { price label touches }
    }
  }
`;

/**
 * The four trading account types: what each is for, the fields its mandate
 * must state, and the defaults the set-up form starts from.
 */
export const GET_TRADING_GOAL_TYPES = gql`
  query TradingGoalTypes {
    tradingGoalTypes {
      goalType
      display
      about
      required
      defaults
    }
  }
`;
